import { cn } from '@consigli/shadcn';
import { type BeamstarStep } from '@consigli/types';
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { useState } from 'react';

import { formatDuration, formatTokens } from '../format';

const KIND_LABELS: Record<string, string> = { thinking: 'Thinking', text: 'Text' };

export const isFailedStep = (step: BeamstarStep) => step.kind === 'tool' && step.ok === false;

const toolStatus = (ok: boolean | null) => {
  if (ok === null) {
    return { text: 'No result', className: 'text-muted-foreground' };
  }
  return ok
    ? { text: 'OK', className: 'text-green-700' }
    : { text: 'Failed', className: 'text-red-700' };
};

const StepRow = ({ step, number }: { step: BeamstarStep; number: number }) => {
  const failed = isFailedStep(step);
  const [isOpen, setIsOpen] = useState(failed);
  const status = step.kind === 'tool' ? toolStatus(step.ok) : null;
  const tokens = step.outputTokens ?? step.reasoningTokens;

  return (
    <li className={cn('border-t first:border-t-0', failed && 'bg-red-50')}>
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        className="grid w-full cursor-pointer grid-cols-[2rem_minmax(0,1fr)_5rem_4.5rem_3.5rem_1rem] items-center gap-3 px-3 py-2 text-left text-sm hover:bg-gray-50"
      >
        <span className="text-xs text-muted-foreground">{number}</span>
        <span
          className={cn('flex min-w-0 items-baseline gap-2', step.agent && 'ml-3 border-l-2 pl-3')}
        >
          <span className="truncate font-mono text-xs font-medium">
            {step.tool ?? KIND_LABELS[step.kind] ?? step.kind}
          </span>
          {step.agent && (
            <span className="truncate text-xs text-muted-foreground">{step.agent}</span>
          )}
        </span>
        <span className={cn('text-xs font-medium', status?.className)}>{status?.text}</span>
        <span className="text-right text-xs text-muted-foreground">
          {step.durationMs !== null && formatDuration(step.durationMs)}
        </span>
        <span className="text-right text-xs text-muted-foreground">
          {tokens !== null && formatTokens(tokens)}
        </span>
        {isOpen ? <ChevronDownIcon className="size-4" /> : <ChevronRightIcon className="size-4" />}
      </button>
      {isOpen && (
        <div className="flex flex-col gap-1 px-3 pb-3 pl-14">
          {step.arguments && (
            <>
              <span className="text-xs font-semibold text-muted-foreground">Arguments</span>
              <pre className="max-h-64 overflow-auto rounded-md border bg-gray-50 p-2 font-mono text-xs">
                {step.arguments}
              </pre>
            </>
          )}
          {step.output && (
            <>
              <span className="text-xs font-semibold text-muted-foreground">Output</span>
              <pre
                className={cn(
                  'max-h-64 overflow-auto rounded-md border bg-gray-50 p-2 font-mono text-xs',
                  failed && 'border-red-200 bg-red-50 text-red-900',
                )}
              >
                {step.output}
              </pre>
            </>
          )}
        </div>
      )}
    </li>
  );
};

interface StepListProps {
  /** One per tool call, thinking block or run of text, in the order the turn made them. */
  steps: BeamstarStep[];
}

export const StepList = ({ steps }: StepListProps) => (
  <ol>
    {steps.map((step, index) => (
      <StepRow key={step.seq} step={step} number={index + 1} />
    ))}
  </ol>
);
